// src/features/records/components/GalleryView/components/MediaLightbox/MediaLightbox.js
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import ImageViewer from './components/ImageViewer';
import VideoPlayer from './components/VideoPlayer';
import { useSignedUrls } from './hooks/useSignedUrls';
import { useGestures } from './hooks/useGestures';
import mapRecordsErrorToKey from '../../../../utils/recordsErrorMapper';
import styles from './MediaLightbox.module.css';
import { runMediaTransition } from '../../transitions/runMediaTransition';

/**
 * MediaLightbox - Full-screen media preview with navigation
 * Uses native <dialog> element for semantic correctness and accessibility
 * 
 * Features:
 * - Native dialog for modal semantics and inert background
 * - Swipe/keyboard navigation
 * - Pinch-to-zoom for images
 * - Video playback controls
 * - Integration with RecordDetail
 */
const MediaLightbox = ({
  items = [],
  initialIndex = 0,
  isOpen = false,
  onClose,
  onOpenItem
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const activeWrapperRef = useRef(null); // receives the morph transform
  const navigateTimeoutRef = useRef(null);
  
  // State management
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [viewPhase, setViewPhase] = useState('viewing'); // opening | viewing | switching | sliding-left | sliding-right | closing
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [videoPaused, setVideoPaused] = useState(true);
  const [carouselOffset, setCarouselOffset] = useState(0); // For swipe gestures
  const [isDragging, setIsDragging] = useState(false); // Track active dragging state
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Current drag offset for active media
  
  // Current media item
  const currentItem = items[activeIndex] || null;
  const hasNext = activeIndex < items.length - 1;
  const hasPrev = activeIndex > 0;
  
  // Get signed URLs for current item and prefetch neighbors
  const {
    mediaUrl,
    previewUrl,
    posterUrl,
    refreshUrl,
    urls,
    loading: signedUrlLoading,
    error: signedUrlError
  } = useSignedUrls(
    items,
    activeIndex,
    isOpen
  );
  const signedUrlErrorKey = signedUrlError
    ? mapRecordsErrorToKey(signedUrlError, { fallbackKey: 'records.views.gallery.errors.load_failed' })
    : null;

  const clearNavigateTimeout = useCallback(() => {
    if (navigateTimeoutRef.current) {
      clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = null;
    }
  }, []);
  
  // Dialog lifecycle management + opening morph
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    
    if (isOpen) {
      // Set state synchronously BEFORE opening dialog to prevent animation glitches
      setActiveIndex(initialIndex);
      setTransform({ scale: 1, x: 0, y: 0 });
      setVideoPaused(true);
      setCarouselOffset(0);
      setViewPhase('opening');
      
      // Store current focus for restoration
      previousFocusRef.current = document.activeElement;
      
      // Open dialog
      if (!dialog.open) {
        dialog.showModal();
        
        // Ensure dialog gets focus on all engines, including iOS Safari
        // This prevents the first focusable element (close button) from receiving focus
        dialog.focus({ preventScroll: true });
      }
      
      // After layout, morph from thumbnail → active media
      const raf = requestAnimationFrame(() => {
        if (activeWrapperRef.current) {
          runMediaTransition({
            fromId: items[initialIndex]?.id,
            toEl: activeWrapperRef.current
          }).finally(() => {
            // ensure we land in viewing state even if transition short-circuits
            setViewPhase('viewing');
          });
        } else {
          setViewPhase('viewing');
        }
      });
      return () => cancelAnimationFrame(raf);
    } else {
      // Close dialog
      if (dialog.open) {
        setViewPhase('closing');
        const id = setTimeout(() => {
          dialog.close();
          setViewPhase('viewing'); // Reset for next open
          
          // Restore focus
          if (previousFocusRef.current && previousFocusRef.current.focus) {
            previousFocusRef.current.focus();
          }
        }, 220);
        return () => clearTimeout(id);
      }
    }
  }, [isOpen, initialIndex, items]);
  
  
  // Navigation handlers
  const navigateTo = useCallback((newIndex, direction = null) => {
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Determine direction if not provided
    if (direction === null) {
      direction = newIndex > activeIndex ? 'left' : 'right';
    }
    
    setViewPhase(direction === 'left' ? 'sliding-left' : 'sliding-right');
    setActiveIndex(newIndex);
    setTransform({ scale: 1, x: 0, y: 0 }); // Reset zoom
    setVideoPaused(true); // Pause video
    setCarouselOffset(0); // Reset carousel offset
    
    clearNavigateTimeout();
    navigateTimeoutRef.current = setTimeout(() => {
      setViewPhase('viewing');
      navigateTimeoutRef.current = null;
    }, 350);
  }, [items.length, activeIndex, clearNavigateTimeout]);
  
  const handlePrevious = useCallback(() => {
    if (hasPrev) navigateTo(activeIndex - 1, 'right');
  }, [hasPrev, activeIndex, navigateTo]);
  
  const handleNext = useCallback(() => {
    if (hasNext) navigateTo(activeIndex + 1, 'left');
  }, [hasNext, activeIndex, navigateTo]);
  
  // Handle zoom and pan
  const handleZoom = useCallback((scale, centerX, centerY) => {
    // Clamp scale between 1 and 4
    const clampedScale = Math.min(Math.max(scale, 1), 4);
    setTransform(prev => ({
      ...prev,
      scale: clampedScale,
      // Reset position when zooming out to 1x
      x: clampedScale === 1 ? 0 : prev.x,
      y: clampedScale === 1 ? 0 : prev.y
    }));
  }, []);
  
  const handlePan = useCallback((dx, dy) => {
    setIsDragging(true);
    
    if (transform.scale > 1) {
      // Pan when zoomed - use absolute values
      setTransform(prev => ({
        ...prev,
        x: dx,
        y: dy
      }));
    } else {
      // Swipe between items when not zoomed
      // Add resistance at boundaries
      let resistedDx = dx;
      if ((activeIndex === 0 && dx > 0) || (activeIndex === items.length - 1 && dx < 0)) {
        resistedDx = dx * 0.3; // Apply resistance
      }
      // Apply offset to both carousel and active media for smooth follow
      setCarouselOffset(resistedDx);
      setDragOffset({ x: resistedDx * 0.1, y: dy * 0.05 }); // Subtle follow effect
    }
  }, [transform.scale, activeIndex, items.length]);
  
  const handlePanEnd = useCallback((dx, velocity) => {
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 }); // Reset media offset
    
    if (transform.scale === 1) {
      // Determine if we should navigate based on distance and velocity
      const threshold = window.innerWidth * 0.3;
      if (Math.abs(dx) > threshold || Math.abs(velocity) > 0.5) {
        if (dx > 0 && hasPrev) {
          handlePrevious();
        } else if (dx < 0 && hasNext) {
          handleNext();
        } else {
          // Snap back
          setCarouselOffset(0);
        }
      } else {
        // Snap back
        setCarouselOffset(0);
      }
    }
  }, [transform.scale, hasPrev, hasNext, handlePrevious, handleNext]);
  
  // Setup gesture handlers with carousel support
  const { handlers } = useGestures({
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrevious,
    onZoom: handleZoom,
    onPan: handlePan,
    onPanEnd: handlePanEnd,
    enabled: isOpen && currentItem?.type === 'image' && viewPhase === 'viewing',
    currentScale: transform.scale
  });
  
  // Prevent Escape from bubbling to parent modals while the lightbox is open
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    
    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
      }
    };
    
    dialog.addEventListener('keydown', handleDialogKeyDown);
    return () => dialog.removeEventListener('keydown', handleDialogKeyDown);
  }, [isOpen]);
  
  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case ' ':
          // Space for video play/pause
          if (currentItem?.type === 'video') {
            e.preventDefault();
            setVideoPaused(prev => !prev);
          }
          break;
        default:
          // ESC is handled by dialog natively
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, currentItem]);
  
  const handleClose = useCallback(() => {
    clearNavigateTimeout();
    // Try to morph back to the thumbnail if it's still on screen
    const el = activeWrapperRef.current;
    if (!el) return onClose?.();
    runMediaTransition({
      fromId: items[activeIndex]?.id,
      toEl: el
    }).finally(() => onClose?.());
  }, [activeIndex, items, onClose, clearNavigateTimeout]);

  useEffect(() => {
    if (!isOpen) {
      clearNavigateTimeout();
    }
    return () => clearNavigateTimeout();
  }, [isOpen, clearNavigateTimeout]);

  const handleCancel = useCallback((event) => {
    event.preventDefault();
    handleClose();
  }, [handleClose]);
  
  // Handle Open Record action
  const handleOpenRecord = useCallback(() => {
    if (currentItem?.recordId && onOpenItem) {
      onClose?.(); // Close lightbox first
      onOpenItem(currentItem.recordId);
    }
  }, [currentItem, onOpenItem, onClose]);
  
  if (!isOpen || items.length === 0) return null;
  
  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={handleCancel}
      aria-modal="true"
      aria-label={t('records.views.gallery.lightbox.dialog_aria')}
      data-phase={viewPhase}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <div className={styles.backdrop} aria-hidden="true" />
      
      {/* Viewport - contains the media carousel */}
      <div 
        className={styles.viewport}
        {...(currentItem?.type === 'image' ? handlers : {})}
      >
        <div className={styles.mediaPlane}>
          {/* Carousel container */}
          <div 
            className={styles.carousel}
            style={{
              transform: `translateX(calc(${-activeIndex * 100}% + ${carouselOffset}px))`,
              transition: isDragging ? 'none' : undefined
            }}
          >
            {/* Render all items for smooth transitions */}
            {items.map((item, index) => {
              // Only render current, previous, and next items with content
              const isVisible = Math.abs(index - activeIndex) <= 1;
              const isActive = index === activeIndex;
              const isInteractive = isActive && viewPhase === 'viewing';
              
              // Get URL for this item (including prefetched)
              let itemUrl = null;
              let itemPosterUrl = null;
              if (isActive) {
                itemUrl = mediaUrl;
                itemPosterUrl = posterUrl;
              } else if (isVisible && urls) {
                // Use prefetched URLs for adjacent items
                if (item.type === 'image') {
                  itemUrl = urls.get(item.storagePath) ||
                           urls.get(item.thumbnails?.['800']) ||
                           urls.get(item.thumbnails?.['400']) ||
                           item.thumbnailUrl;
                } else if (item.type === 'video') {
                  itemUrl = urls.get(item.storagePath);
                  itemPosterUrl = urls.get(item.thumbnails?.poster);
                }
              }
              const isSignedUrlResolutionFailure = isActive &&
                !signedUrlLoading &&
                !!signedUrlError &&
                (
                  (item.type === 'image' && !itemUrl && !previewUrl) ||
                  (item.type === 'video' && !itemUrl)
                );
              
              return (
                <div key={item.id} className={styles.mediaContainer}>
                  <div
                    className={styles.activeWrapper}
                    ref={isActive ? activeWrapperRef : null}
                  >
                    {isVisible && (
                      isSignedUrlResolutionFailure ? (
                        <div className={styles.mediaError} role="alert" aria-live="polite">
                          <p>{t(signedUrlErrorKey || 'records.views.gallery.errors.load_failed')}</p>
                        </div>
                      ) : (
                        item.type === 'image' ? (
                          <ImageViewer
                            item={item}
                            url={itemUrl}
                            previewUrl={isActive ? previewUrl : null}
                            transform={isActive ? transform : { scale: 1, x: 0, y: 0 }}
                            dragOffset={isActive ? dragOffset : { x: 0, y: 0 }}
                            isDragging={isActive && isDragging}
                            isActive={isInteractive}
                            canLoad={isVisible}
                            onError={() => {
                              if (isActive) {
                                const path = item.storagePath ||
                                            item.thumbnails?.['800'] ||
                                            item.thumbnails?.['400'];
                                if (path) refreshUrl(path);
                              }
                            }}
                          />
                        ) : (
                          <VideoPlayer
                            item={item}
                            url={itemUrl}
                            posterUrl={itemPosterUrl}
                            isActive={isInteractive}
                            isPaused={!isInteractive || videoPaused}
                            onPlay={() => isInteractive && setVideoPaused(false)}
                            onPause={() => isInteractive && setVideoPaused(true)}
                            onError={() => {
                              if (isActive && item.storagePath) {
                                refreshUrl(item.storagePath);
                              }
                            }}
                          />
                        )
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Chrome - UI controls */}
      <div className={styles.chrome}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.counter}>
              {t('records.views.gallery.lightbox.counter', {
                current: activeIndex + 1,
                total: items.length,
              })}
            </span>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              aria-label={t('common.close')}
              type="button"
            >
              <X size={24} />
            </button>
          </div>
        </header>
        
        {/* Side Navigation */}
        <nav className={styles.sideNav}>
          <button
            className={styles.navButton}
            onClick={handlePrevious}
            disabled={!hasPrev}
            aria-label={t('records.views.gallery.lightbox.previous_aria')}
            type="button"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            className={styles.navButton}
            onClick={handleNext}
            disabled={!hasNext}
            aria-label={t('records.views.gallery.lightbox.next_aria')}
            type="button"
          >
            <ChevronRight size={32} />
          </button>
        </nav>
        
        {/* Footer */}
        {onOpenItem && currentItem?.recordId && (
          <footer className={styles.footer}>
            <button
              className={styles.openItemButton}
              onClick={handleOpenRecord}
              type="button"
            >
              {t('records.views.gallery.actions.open_record')}
            </button>
          </footer>
        )}
      </div>
    </dialog>
  );
};

MediaLightbox.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    recordId: PropTypes.string,
    type: PropTypes.oneOf(['image', 'video']).isRequired,
    thumbnails: PropTypes.shape({
      '400': PropTypes.string,
      '800': PropTypes.string,
      poster: PropTypes.string
    }),
    thumbnailUrl: PropTypes.string,
    posterUrl: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    duration: PropTypes.number
  })).isRequired,
  initialIndex: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onOpenItem: PropTypes.func
};

export default MediaLightbox;
